import { Link, useNavigate } from "react-router-dom";
import { Cpu, Bot, Workflow, GraduationCap, Shield, Activity, BarChart3, ArrowUpRight, ArrowDown, Check, Users } from "lucide-react";
import { Seo } from "@/components/Seo";
import { Navbar, goToContact } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { SectionHeading, Reveal } from "@/components/Reveal";
import { SERVICES } from "@/data/content";

const ICONS = {
  cpu: Cpu,
  bot: Bot,
  workflow: Workflow,
  graduation: GraduationCap,
  shield: Shield,
  activity: Activity,
  chart: BarChart3,
};

export default function ServicesPage() {
  const navigate = useNavigate();
  return (
    <div className="bg-[#14163C] min-h-screen" data-testid="services-page">
      <Seo
        title="Servicios de IA y Ciberseguridad | Cibermedida"
        description="Consultoría e implantación de IA, analítica de datos y modelos predictivos, agentes conversacionales, automatización de procesos, formación en IA, gobierno del dato y SOC 24/7."
      />
      <Navbar />
      <main className="pt-32 pb-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <SectionHeading
            testId="services-heading"
            kicker="Servicios"
            title="De la idea al agente en producción"
            description="Un catálogo modular centrado en inteligencia artificial: contrata una consultoría puntual o delega en nosotros la transformación completa, desde el análisis del problema y la preparación de los datos hasta el modelo vigilado en producción, siempre sobre una base segura."
          />
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="services-index">
            {SERVICES.map((s, i) => {
              const Icon = ICONS[s.icon];
              return (
                <Reveal key={s.title} delay={i * 0.05}>
                  <Link
                    to={`/servicios#${s.slug}`}
                    data-testid={`service-card-${i}`}
                    className="card-glow group h-full bg-[#242764] border border-[#3FE0F2]/15 rounded-2xl p-5 flex items-center gap-4"
                  >
                    <div className="w-11 h-11 shrink-0 rounded-xl bg-[#3FE0F2]/10 border border-[#3FE0F2]/25 flex items-center justify-center text-[#3FE0F2]">
                      <Icon size={20} />
                    </div>
                    <span className="flex-1 text-sm font-semibold text-[#EEFBFF] group-hover:text-[#3FE0F2] transition-colors">{s.title}</span>
                    <ArrowDown size={16} className="text-[#7D86C2] group-hover:text-[#3FE0F2] transition-colors" />
                  </Link>
                </Reveal>
              );
            })}
          </div>

          <div className="mt-24 flex flex-col gap-28">
            {SERVICES.map((s, i) => {
              const Icon = ICONS[s.icon];
              const flip = i % 2 === 1;
              return (
                <section key={s.slug} id={s.slug} className="scroll-mt-28 grid lg:grid-cols-2 gap-10 lg:gap-16 items-center" data-testid={`service-detail-${i}`}>
                  <Reveal className={flip ? "lg:order-2" : ""}>
                    <div className="relative rounded-2xl overflow-hidden border border-[#3FE0F2]/20">
                      <img src={s.image} alt={s.title} width="1400" height="933" loading="lazy" className="w-full h-[300px] sm:h-[420px] object-cover" />
                      <div className="absolute inset-0 bg-gradient-to-tr from-[#14163C]/75 via-[#14163C]/10 to-transparent" />
                      <div className="absolute left-5 bottom-5 flex items-center gap-3">
                        <span className="w-11 h-11 rounded-xl bg-[#14163C]/80 border border-[#3FE0F2]/40 flex items-center justify-center text-[#3FE0F2]">
                          <Icon size={20} />
                        </span>
                        <span className="font-mono-cyber text-xs uppercase tracking-[0.2em] text-[#EEFBFF]">Servicio 0{i + 1}</span>
                      </div>
                    </div>
                  </Reveal>
                  <Reveal delay={0.08} className={flip ? "lg:order-1" : ""}>
                    <h2 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight text-[#EEFBFF] leading-tight">{s.title}</h2>
                    <p className="mt-4 text-base text-[#D8E2FF] leading-relaxed">{s.text}</p>
                    {s.detail?.map((para) => (
                      <p key={para.slice(0, 24)} className="mt-4 text-sm text-[#B9C3EC] leading-relaxed">{para}</p>
                    ))}
                    {s.points && (
                      <ul className="mt-6 grid sm:grid-cols-2 gap-3">
                        {s.points.map((pt) => (
                          <li key={pt} className="flex gap-2 text-[13px] text-[#D8E2FF] leading-snug">
                            <Check size={15} className="text-[#3FE0F2] shrink-0 mt-0.5" />
                            {pt}
                          </li>
                        ))}
                      </ul>
                    )}
                    {s.forWho && (
                      <p className="mt-6 flex gap-2 text-sm text-[#94A4D0] border-l-2 border-[#3FE0F2]/40 pl-4">
                        <Users size={16} className="text-[#3FE0F2] shrink-0 mt-0.5" />
                        <span><strong className="text-[#EEFBFF] font-semibold">Para quién: </strong>{s.forWho}</span>
                      </p>
                    )}
                    <button
                      onClick={() => goToContact(navigate, s.title)}
                      className="mt-7 inline-flex items-center gap-2 text-sm font-semibold text-[#14163C] bg-[#3FE0F2] rounded-full px-6 py-3 hover:bg-[#7FEBF8] transition-colors group"
                    >
                      Solicitar información
                      <ArrowUpRight size={15} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                    </button>
                  </Reveal>
                </section>
              );
            })}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
